import { useLayoutEffect, useRef } from 'react';

import { useTracking } from '@change-corgi/core-react/tracking';

import { useStarterDashboardV2PageData } from '#src/app/pages/starterDashboardV2/pageContext';
import { differenceInDays } from '#src/app/shared/utils/dates';

const EVENT_NAME = 'petitions_dashboard_view';

type PageViewData = {
	score: number;
	category_tags: string[];
	days_since_creation: number;
	context?: string;
};

/**
 * Hook to track page views in the dashboard. It handles both initial page load and section changes.
 * For the home section, it always tracks the view to ensure proper analytics.
 * For other sections, it only tracks when the section changes.
 *
 * @param section - Current dashboard section identifier
 */
export function useTrackPageView(section?: string): void {
	const {
		data: { petition },
	} = useStarterDashboardV2PageData();

	const track = useTracking();
	const didTrackRef = useRef(false);
	const lastSectionRef = useRef<string | undefined>(undefined);

	useLayoutEffect(() => {
		if (section === lastSectionRef.current) return;
		if (!petition.createdAt) return;

		const createdAt = new Date(petition.createdAt);
		const daysSinceCreation = !Number.isNaN(createdAt.getTime()) ? differenceInDays(new Date(), createdAt) : 0;

		const trackingData: PageViewData = {
			score: petition.score,
			category_tags: petition.tagsConnection.nodes.map((t) => t.name).filter(Boolean),
			days_since_creation: daysSinceCreation,
			context: section ?? 'petitions_dashboard',
		};

		void track(EVENT_NAME, trackingData);

		didTrackRef.current = true;
		lastSectionRef.current = section;
	}, [section, track, petition, petition.createdAt, petition.score, petition.tagsConnection]);
}
